/**
 * Section completion, two deliberately separate notions:
 *   - The **gate** (`incompleteRequiredSections`): can the consultant finalize?
 *     Only REQUIRED fields count. Mirrors the backend's field-level
 *     `schema.incomplete_required_intake`, so the two can't disagree.
 *   - The **indicator** (`sectionCompletion`): the rail dot. "Complete" once the
 *     required + recommended fields are filled; optional fields don't count.
 */
import type { FieldDef, SectionDef } from './intakeSchema'

export type SectionValues = Record<string, unknown>

/** A captured value counts as filled when non-empty. Mirrors backend `schema.value_filled`. */
export function isFilled(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (typeof v === 'string') return v.trim() !== ''
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'object') return Object.keys(v as object).length > 0
  return true
}

const isRequired = (f: FieldDef) => f.priority === 'required'
const isMeaningful = (f: FieldDef) => f.priority === 'required' || f.priority === 'recommended'

/** Required-field progress for a section (drives the finish gate + red badge). */
export function sectionRequiredStats(
  section: SectionDef,
  values: SectionValues | undefined,
): { filledRequired: number; totalRequired: number } {
  const required = section.fields.filter(isRequired)
  const filled = required.filter((f) => isFilled(values?.[f.key])).length
  return { filledRequired: filled, totalRequired: required.length }
}

/** Required fields still empty across `sections` — the rail legend's red count. */
export function requiredMissing(
  sections: readonly SectionDef[],
  captured: Record<string, SectionValues | undefined>,
): number {
  return sections.reduce((n, s) => {
    const st = sectionRequiredStats(s, captured[s.key])
    return n + st.totalRequired - st.filledRequired
  }, 0)
}

/** Overall field progress (filled / total across all of a section's fields). */
export function sectionFieldStats(
  section: SectionDef,
  values: SectionValues | undefined,
): { filled: number; total: number } {
  const filled = section.fields.filter((f) => isFilled(values?.[f.key])).length
  return { filled, total: section.fields.length }
}

/** Rail status: 'complete' once the required + recommended fields are filled
 *  (all fields, for a section with neither). */
export function sectionCompletion(
  section: SectionDef,
  values: SectionValues | undefined,
): 'empty' | 'partial' | 'complete' {
  const anyFilled = section.fields.some((f) => isFilled(values?.[f.key]))
  if (!anyFilled) return 'empty'
  const meaningful = section.fields.filter(isMeaningful)
  const target = meaningful.length > 0 ? meaningful : section.fields
  const targetFilled = target.filter((f) => isFilled(values?.[f.key])).length
  return targetFilled === target.length ? 'complete' : 'partial'
}

/** Sections still missing a required field — the finish gate (empty = done). */
export function incompleteRequiredSections(
  sections: readonly SectionDef[],
  captured: Record<string, SectionValues> | undefined,
): SectionDef[] {
  const cf = captured ?? {}
  return sections.filter((s) => {
    const stats = sectionRequiredStats(s, cf[s.key])
    return stats.totalRequired > 0 && stats.filledRequired < stats.totalRequired
  })
}
